import Dialog from '@mui/material/Dialog';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useDeleteUsersMutation, useUpdateUsersMutation } from '@/api/v1';
import { ConfirmationModal } from '@/components/molecules/ConfirmationModal';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { EditUserForm } from '@/components/organisms/EditUserForm';
import { UpdateUserFormData, UpdateUserRequest } from '@/DTOs/users';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';

export const EditUserModal: React.FC = (): React.ReactElement => {
  const { userId } = useParams();

  const [updateUsers] = useUpdateUsersMutation();
  const [deleteUsers] = useDeleteUsersMutation();

  const toast = useToast();
  const tr = useTranslator();
  const navigate = useNavigate();

  const [wantsRemove, setWantsRemove] = React.useState<boolean>(false);

  const onClose = React.useCallback((): void => {
    navigate('..');
  }, [navigate]);

  const onUpdateUser = React.useCallback(
    async (formData: UpdateUserFormData): Promise<void> => {
      if (!userId) {
        toast.show('User ID is missing', 'error');
        return;
      }

      const updateUserRequest: UpdateUserRequest = {
        id: userId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        group: formData.group,
        department: formData.department,
      };

      try {
        await updateUsers(updateUserRequest).unwrap();

        toast.show('User updated successfully', 'success');
        onClose();
      } catch (error) {
        console.error('Error updating user:', error);
        toast.show('Error updating user', 'error');
      }
    },
    [userId, updateUsers, onClose, toast],
  );

  const onRemoveUser = React.useCallback((): void => {
    setWantsRemove(true);
  }, []);

  const onConfirmRemove = React.useCallback(async (): Promise<void> => {
    if (!userId) {
      toast.show('User ID is missing', 'error');
      return;
    }

    try {
      await deleteUsers(userId).unwrap();
      toast.show('User removed successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Error removing user:', error);
      toast.show('Error removing user', 'error');
    }
  }, [userId, deleteUsers, onClose, toast]);

  const onCancelRemove = React.useCallback((): void => {
    setWantsRemove(false);
  }, []);

  React.useEffect((): VoidFunction => {
    return (): void => {
      toast.remove();
    };
  }, [toast]);

  return (
    <>
      <Dialog open={true}>
        <ApplicationModal title={tr('MANAGE_USER')} variant="prominent" onClose={onClose}>
          <EditUserForm userId={userId} onSubmit={onUpdateUser} onRemove={onRemoveUser} />
        </ApplicationModal>
      </Dialog>
      <ConfirmationModal
        open={wantsRemove}
        title={tr('REMOVE')}
        description={tr('THIS_ACTION_IS_NOT_REVERSIBLE')}
        confirmButtonText={tr('REMOVE')}
        abortButtonText={tr('CANCEL')}
        onConfirm={onConfirmRemove}
        onAbort={onCancelRemove}
      />
    </>
  );
};
